import { Box, Chip, DialogContent, Divider, Typography } from "@mui/material"
import { ArchiveOutlined, HomeWorkOutlined, PersonAddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"

import type { NewRequestFormValues } from "../../types"
import { formatFullName, formatProductLabel } from "../../utils/format"

import { NewRequestStepActions } from "./NewRequestStepActions"
import { PRODUCT_BADGE_BY_PATH } from "./pricing/constants"
import { StepSectionLabel } from "./StepSectionLabel"

import type { Location } from "@/graphql/codegen/graphql"
import { AddressFormat } from "@/types/common"
import { formatLocationAddress } from "@/utils/utils"

interface ReviewRequestStepProps {
  formValues: NewRequestFormValues
  onBack: () => void
  onContinue: () => void
  continueLabel?: string
  continueDisabled?: boolean
}

export const ReviewRequestStep: FC<ReviewRequestStepProps> = ({
  formValues,
  onBack,
  onContinue,
  continueLabel = "Continue To Email",
  continueDisabled = false,
}) => {
  const { customer, requesters, products = [], pricingByProductId } = formValues
  const quoteProducts = products.filter((product) => product.requestQuote)

  const customerLabel = [
    customer.company.name,
    formatLocationAddress(customer.billingLocation as Location | null | undefined, AddressFormat.LocationAndAddress),
  ]
    .filter(Boolean)
    .join(" — ")

  return (
    <>
      <DialogContent className='flex flex-col gap-6 px-10 py-6'>
        <Box className='flex flex-col gap-2'>
          <StepSectionLabel icon={<HomeWorkOutlined />} label='Customer' />
          <Typography className='text-sm text-gray-800'>{customerLabel}</Typography>
        </Box>

        <Divider />

        <Box className='flex flex-col gap-2'>
          <StepSectionLabel icon={<PersonAddOutlined />} label='Requester' />
          <Box className='flex flex-wrap gap-2'>
            {requesters.map((requester) => (
              <Chip
                key={requester.contactId}
                size='small'
                label={formatFullName(requester.firstName, requester.lastName)}
                className='rounded bg-gray-200'
              />
            ))}
          </Box>
        </Box>

        <Divider />

        <Box className='flex flex-col gap-2'>
          <StepSectionLabel icon={<ArchiveOutlined />} label='Products Requested' />
          <Box className='flex flex-col gap-3'>
            {quoteProducts.map(({ product }) => {
              const pricing = pricingByProductId?.get(product.productId)
              if (!pricing) return null
              const badge = PRODUCT_BADGE_BY_PATH[pricing.path]
              return (
                <Box key={product.productId} className='flex flex-col gap-1'>
                  <Typography className='text-sm text-gray-800'>{formatProductLabel(product)}</Typography>
                  <Box className='flex flex-wrap gap-2'>
                    <Chip
                      size='small'
                      label={badge.label}
                      className={`rounded ${badge.className}`}
                      data-testid={`sales.review-request-step.pricing-path-badge-${product.productId}`}
                    />
                  </Box>
                </Box>
              )
            })}
          </Box>
        </Box>
      </DialogContent>

      <NewRequestStepActions
        onCancel={onBack}
        onContinue={onContinue}
        cancelLabel='Go Back'
        continueLabel={continueLabel}
        continueDisabled={continueDisabled}
        cancelTestId='sales.review-request-step.back-button'
        continueTestId='sales.review-request-step.continue-button'
      />
    </>
  )
}
